import { useEffect, useState } from 'react';

type Options = {
  /** Cantidad fuente (numero ya validado, >= min). */
  value: number;
  /** Se llama con el numero validado cada vez que cambia. */
  onCommit: (n: number) => void;
  max: number;
  min?: number;
};

/**
 * Maneja un input de cantidad numerico que SI se puede vaciar para escribir otro
 * numero (p. ej. 200) sin que quede un digito pegado. Mientras se edita guarda un
 * texto local libre (puede quedar vacio); al salir (blur) lo normaliza al minimo
 * si quedo vacio. Asi el usuario borra y tipea en vez de dar clic 200 veces.
 *
 * Uso: render un <input type="text" inputMode="numeric" value={text}
 * onChange={e => onChange(e.target.value)} onBlur={onBlur} />.
 */
export function useQuantityInput({ value, onCommit, max, min = 1 }: Options) {
  const [text, setText] = useState(String(value));

  // Resync cuando la cantidad cambia desde afuera (botones +/-, reset del carrito).
  useEffect(() => {
    setText(String(value));
  }, [value]);

  const clamp = (n: number) => Math.min(max, Math.max(min, n));

  const onChange = (raw: string) => {
    if (raw === '') {
      // Permite dejar el campo vacio mientras se escribe.
      setText('');
      return;
    }
    if (!/^\d+$/.test(raw)) return; // solo digitos
    const n = clamp(parseInt(raw, 10));
    setText(String(n));
    onCommit(n);
  };

  const onBlur = () => {
    if (text === '') {
      setText(String(min));
      onCommit(min);
    }
  };

  return { text, onChange, onBlur };
}
